/**
 * A gathering, from either side: the node's own (backend: pods/gatherings,
 * at /api/gathering) or an app instance's (backend: sdk_apps/gathering.py,
 * through the instance's views and verbs). Pass `instanceId` for the app;
 * payloads and responses are the same either way, so the screens are too.
 *
 *   const member = useMemberGathering({ pollMs, instanceId });
 *   //   { data, loading, error, saving, refresh, setMyGroups(ids) }
 *   const admin = useAdminGathering({ enabled, instanceId });
 *   //   { data, loading, error, refresh, updateSettings, createGroup,
 *   //     updateGroup, deleteGroup, createTopic, updateTopic, deleteTopic,
 *   //     reorderTopics, replaceFlow, startFlow, stopFlow, pauseFlow,
 *   //     resumeFlow, extendStep, nextStep, importConfig, resetGathering,
 *   //     clearPicks, removeGuests(scope), publish, unpublish }
 *   const { people } = useNodePeople({ enabled, includePending, instanceId });
 *   //   every person the admin can seat when the pool is "Everyone": the
 *   //   node's people, or the people of the app's hosts
 *   const status = useGatheringStatus({ enabled });
 *   //   'closed' | 'waiting' | 'open' | null (unknown yet) — for chrome
 *   //   like the nav bar that only needs to know whether it's live
 *
 * The member view re-polls (every 15s by default; the live flow page asks
 * for faster) so a flow started, paused or moved on by the admin shows up
 * without a reload. It acts as useGatheringIdentity() — a
 * guest who joined with the passcode, else the signed-in user — and sends a
 * guest whose pass the backend refuses back to /gathering/join. An app
 * gathering has no guests: its people are those of its hosts, signed in.
 *
 * Member picks are applied optimistically — a checkbox shouldn't wait on a
 * round trip — and rolled back if the save fails. Admin mutations resolve to
 * the server's state and replace `data` with it.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { useGatheringIdentity } from './use_gathering_identity';
import { clearGuest } from '../utils/guest_session';
import { useUnclaimedPeople } from './use_unclaimed_people';
import { parseServerTime } from '../utils/gathering';

// Where the gathering lives. The node's own, at /api/gathering, takes the
// acting user as a `user_id` query param (the pod's convention); an app
// instance's goes through /api/apps/{id}/views and /verbs, as the signed-in
// person. One method per thing the screens do.
function podTransport(api, userId) {
    const params = { params: { user_id: userId } };
    return {
        me: () => api.get('/api/gathering', params),
        pick: ids => api.put('/api/gathering/me', { group_ids: ids }, params),
        admin: () => api.get('/api/gathering/admin', params),
        settings: patch => api.patch('/api/gathering/admin', patch, params),
        groupCreate: body => api.post('/api/gathering/admin/groups', body, params),
        groupUpdate: (id, patch) => api.patch(`/api/gathering/admin/groups/${id}`, patch, params),
        groupDelete: id => api.del(`/api/gathering/admin/groups/${id}`, params),
        topicCreate: text => api.post('/api/gathering/admin/topics', { text }, params),
        topicUpdate: (id, text) => api.patch(`/api/gathering/admin/topics/${id}`, { text }, params),
        topicDelete: id => api.del(`/api/gathering/admin/topics/${id}`, params),
        topicsOrder: ids => api.put('/api/gathering/admin/topics/order', { ids }, params),
        publish: steps => api.put('/api/gathering/admin/rooms', { steps }, params),
        unpublish: () => api.del('/api/gathering/admin/rooms', params),
        flow: steps => api.put('/api/gathering/admin/flow', { steps }, params),
        flowStart: () => api.post('/api/gathering/admin/flow/start', undefined, params),
        flowStop: () => api.post('/api/gathering/admin/flow/stop', undefined, params),
        flowPause: () => api.post('/api/gathering/admin/flow/pause', undefined, params),
        flowResume: () => api.post('/api/gathering/admin/flow/resume', undefined, params),
        flowNext: stepId => api.post('/api/gathering/admin/flow/next', { step_id: stepId }, params),
        flowExtend: (stepId, minutes) => api.post('/api/gathering/admin/flow/extend', { step_id: stepId, minutes }, params),
        importConfig: config => api.post('/api/gathering/admin/import', config, params),
        reset: () => api.post('/api/gathering/admin/reset', undefined, params),
        clearPicks: () => api.del('/api/gathering/admin/picks', params),
        removeGuests: scope => api.post('/api/gathering/admin/guests/remove', { scope }, params),
        // The node's people come from useNodePeople's own sources (Browse's).
        people: null,
    };
}

function appTransport(api, instanceId) {
    const verb = (name, payload) => api.post(`/api/apps/${instanceId}/verbs/${name}`, payload ?? {}).then(r => r.result);
    const view = name => api.get(`/api/apps/${instanceId}/views/${name}`).then(r => r.data);
    return {
        me: () => view('me'),
        pick: ids => verb('pick', { group_ids: ids }),
        admin: () => view('admin'),
        settings: patch => verb('settings', patch),
        groupCreate: body => verb('group_create', body),
        groupUpdate: (id, patch) => verb('group_update', { id, ...patch }),
        groupDelete: id => verb('group_delete', { id }),
        topicCreate: text => verb('topic_create', { text }),
        topicUpdate: (id, text) => verb('topic_update', { id, text }),
        topicDelete: id => verb('topic_delete', { id }),
        topicsOrder: ids => verb('topics_order', { ids }),
        publish: steps => verb('publish', { steps }),
        unpublish: () => verb('unpublish'),
        flow: steps => verb('flow', { steps }),
        flowStart: () => verb('flow_start'),
        flowStop: () => verb('flow_stop'),
        flowPause: () => verb('flow_pause'),
        flowResume: () => verb('flow_resume'),
        flowNext: stepId => verb('flow_next', { step_id: stepId }),
        flowExtend: (stepId, minutes) => verb('flow_extend', { step_id: stepId, minutes }),
        importConfig: config => verb('import', config),
        reset: () => verb('reset'),
        clearPicks: () => verb('clear_picks'),
        removeGuests: () => Promise.reject(new Error('An app gathering has no guests')),
        people: () => view('people').then(d => d.people || []),
    };
}

function useTransport(instanceId, userId) {
    const api = useApi();
    return useMemo(() => (instanceId ? appTransport(api, instanceId) : podTransport(api, userId)), [api, instanceId, userId]);
}

export function useMemberGathering({ pollMs = 15000, instanceId = null } = {}) {
    const navigate = useNavigate();
    const { userId, isGuest } = useGatheringIdentity();
    const t = useTransport(instanceId, userId);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        if (!userId) return Promise.resolve();
        return t.me()
            .then(d => {
                // A guest the host removed has no person any more: the
                // backend can't tell that id from any other unknown one, so
                // the page has to notice and send them back to the door.
                if (isGuest && d && d.my_person_id == null) {
                    clearGuest();
                    navigate('/gathering/join?removed=1', { replace: true });
                    return;
                }
                setData(d);
                setError(null);
            })
            .catch(err => {
                // The pass ran out (or the guest was reaped) mid-gathering.
                if (isGuest && err.status === 403) {
                    clearGuest();
                    navigate('/gathering/join?expired=1', { replace: true });
                    return;
                }
                setError(err.message);
            })
            .finally(() => setLoading(false));
    }, [t, userId, isGuest, navigate]);

    useEffect(() => { load(); }, [load]);

    // The admin starts and stops the flow from their own screen, so poll —
    // gently — to notice. The countdown itself ticks locally.
    useEffect(() => {
        const timer = setInterval(() => { if (!document.hidden) load(); }, pollMs);
        return () => clearInterval(timer);
    }, [load, pollMs]);

    const setMyGroups = useCallback((groupIds) => {
        const before = data;
        setData(d => (d ? { ...d, my_group_ids: groupIds } : d));
        setSaving(true);
        return t.pick(groupIds)
            .then(setData)
            .catch(err => { setData(before); message.error(err.message || 'Could not save your groups'); })
            .finally(() => setSaving(false));
    }, [t, data]);

    return { data, loading, error, saving, refresh: load, setMyGroups };
}

export function useAdminGathering({ enabled = true, instanceId = null } = {}) {
    const { user } = useAuth();
    const userId = user?.id;
    const t = useTransport(instanceId, userId);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!enabled || !userId) { setLoading(false); return Promise.resolve(); }
        return t.admin()
            .then(d => { setData(d); setError(null); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [t, userId, enabled]);

    useEffect(() => { load(); }, [load]);

    // Every admin write either returns the whole admin state (settings, rooms)
    // or touches groups, after which a reload keeps memberships consistent.
    const wrap = (promise, { reload = false } = {}) => promise
        .then(d => (reload ? load().then(() => d) : (setData(d), d)))
        .catch(err => { message.error(err.message || 'Something went wrong'); throw err; });

    return {
        data,
        loading,
        error,
        refresh: load,
        updateSettings: patch => wrap(t.settings(patch)),
        createGroup: body => wrap(t.groupCreate(body), { reload: true }),
        // A rename or seat change touches one row — patch it in place rather
        // than reloading, so typing into the ideal room stays smooth.
        updateGroup: (id, patch) => t.groupUpdate(id, patch)
            .then(g => { setData(d => (d ? { ...d, groups: d.groups.map(x => (x.id === g.id ? g : x)) } : d)); return g; })
            .catch(err => { message.error(err.message || 'Could not save the group'); throw err; }),
        deleteGroup: id => wrap(t.groupDelete(id), { reload: true }),
        // Topics: each write patches `data.topics` in place.
        createTopic: text => t.topicCreate(text)
            .then(topic => { setData(d => (d ? { ...d, topics: [...d.topics, topic] } : d)); return topic; })
            .catch(err => { message.error(err.message || 'Could not add the topic'); throw err; }),
        updateTopic: (id, text) => t.topicUpdate(id, text)
            .then(topic => { setData(d => (d ? { ...d, topics: d.topics.map(x => (x.id === topic.id ? topic : x)) } : d)); return topic; })
            .catch(err => { message.error(err.message || 'Could not save the topic'); throw err; }),
        deleteTopic: id => t.topicDelete(id)
            .then(() => setData(d => (d ? { ...d, topics: d.topics.filter(x => x.id !== id) } : d)))
            .catch(err => { message.error(err.message || 'Could not delete the topic'); throw err; }),
        reorderTopics: ids => {
            // Optimistic: moving a row shouldn't wait on the network.
            setData(d => (d ? { ...d, topics: ids.map(id => d.topics.find(x => x.id === id)).filter(Boolean) } : d));
            return t.topicsOrder(ids)
                .then(topics => setData(d => (d ? { ...d, topics } : d)))
                .catch(err => { message.error(err.message || 'Could not reorder topics'); load(); throw err; });
        },
        // steps: [{ step_id, rooms: [{ person_ids, quota }] }] — every breakout round at once.
        publish: steps => wrap(t.publish(steps)),
        // steps: [{ id?, kind, duration_minutes, seed }] — the whole flow, in order.
        replaceFlow: steps => wrap(t.flow(steps)),
        startFlow: () => wrap(t.flowStart()),
        stopFlow: () => wrap(t.flowStop()),
        pauseFlow: () => wrap(t.flowPause()),
        resumeFlow: () => wrap(t.flowResume()),
        resetGathering: () => wrap(t.reset()),
        // Everyone's picks go, groups stay — members pick again.
        clearPicks: () => wrap(t.clearPicks()),
        // scope: 'expired' (tidy up passes that ran out) | 'all' (end every guest pass now)
        removeGuests: scope => wrap(t.removeGuests(scope)),
        importConfig: config => wrap(t.importConfig(config)),
        nextStep: stepId => wrap(t.flowNext(stepId)),
        extendStep: (stepId, minutes) => wrap(t.flowExtend(stepId, minutes)),
        unpublish: () => wrap(t.unpublish()),
    };
}

/**
 * Every person an admin can seat when the pool is "Everyone". For the
 * node's gathering: listed people, plus the unlisted ones a superadmin can
 * see (same sources as Browse); people with no account (user_id null)
 * couldn't join a call, so they're left out unless asked for. For an app
 * gathering: the people of the entities it is installed in, from the app's
 * own `people` view.
 */
export function useNodePeople({ enabled = true, includePending = false, instanceId = null } = {}) {
    const api = useApi();
    const { allPeople } = useUnclaimedPeople();
    const [listed, setListed] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!enabled) { setLoading(false); return undefined; }
        let alive = true;
        const request = instanceId
            ? appTransport(api, instanceId).people()
            : api.get('/api/entities', { params: { listed: true, entity_type: 'person' } });
        request
            .then(d => { if (alive) setListed(Array.isArray(d) ? d : []); })
            .catch(() => { if (alive) setListed([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [api, enabled, instanceId]);

    const people = useMemo(() => {
        if (instanceId) return listed;
        const byId = new Map(listed.filter(e => e.entity_type === 'person').map(e => [e.id, e]));
        allPeople.forEach(p => { if (!byId.has(p.id)) byId.set(p.id, p); });
        return [...byId.values()].filter(p => includePending || p.user_id);
    }, [listed, allPeople, includePending, instanceId]);

    return { people, loading };
}

/**
 * Just the gathering's status, polled for chrome that only needs to know
 * whether it's live (the nav bar) — not the groups/schedule payload that
 * comes with it. Same endpoint and poll cadence as useMemberGathering, but
 * without its guest-session/redirect plumbing, since that chrome never
 * renders for a guest (GatheringPage has no NavBar — see RouteMap.js).
 */
export function useGatheringStatus({ enabled = true, pollMs = 15000 } = {}) {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!enabled || !userId) return undefined;
        let alive = true;
        const load = () => api.get('/api/gathering', { params: { user_id: userId } })
            .then(d => { if (alive) setStatus(d?.status ?? null); })
            .catch(() => {});
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, pollMs);
        return () => { alive = false; clearInterval(timer); };
    }, [api, userId, enabled, pollMs]);

    return status;
}

/**
 * "Now" in ms on the server's clock (from a response's `server_now`), ticking
 * every second while `active` — so a countdown is right even when the
 * viewer's clock isn't.
 */
export function useServerClock(serverNow, active) {
    const offset = useMemo(() => (serverNow ? parseServerTime(serverNow) - Date.now() : 0), [serverNow]);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [active]);
    return now + offset;
}
